import { backofficeFetch } from '@/services/backoffice-api';
import type {
  BackofficeLink,
  BackofficeLinkAccessesResponse,
  BackofficeLinkSlugAvailabilityResponse,
  BackofficeLinksResponse,
  BackofficeStudySearchResponse,
  CreateBackofficeLinkInput,
  RestoreBackofficeLinkInput,
  UpdateBackofficeLinkInput,
  UpdateBackofficeLinkQrCodeInput,
} from './types';

async function readJson<T>(response: Response, fallback: string): Promise<T> {
  const body = (await response.json().catch(() => null)) as { error?: string } | null;
  if (!response.ok) {
    throw new Error(body?.error ?? fallback);
  }
  return body as T;
}

export async function fetchBackofficeLinks(
  searchParams: URLSearchParams,
  signal?: AbortSignal
): Promise<BackofficeLinksResponse> {
  const response = await backofficeFetch(`/links?${searchParams.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });
  return readJson<BackofficeLinksResponse>(response, `Failed to load links (${response.status})`);
}

export async function createBackofficeLink(input: CreateBackofficeLinkInput) {
  const response = await backofficeFetch('/links', {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return readJson<{ link: BackofficeLink }>(response, `Failed to create link (${response.status})`);
}

export async function fetchBackofficeLink(slug: string, signal?: AbortSignal) {
  const response = await backofficeFetch(`/links/${encodeURIComponent(slug)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });
  return readJson<{ link: BackofficeLink }>(response, `Failed to load link (${response.status})`);
}

export async function fetchBackofficeLinkAccesses(slug: string, signal?: AbortSignal) {
  const response = await backofficeFetch(`/links/${encodeURIComponent(slug)}/accesses`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });
  return readJson<BackofficeLinkAccessesResponse>(
    response,
    `Failed to load link usage (${response.status})`
  );
}

export async function checkBackofficeLinkSlugAvailability(
  slug: string,
  signal?: AbortSignal
): Promise<BackofficeLinkSlugAvailabilityResponse> {
  const params = new URLSearchParams({ slug });
  const response = await backofficeFetch(`/links/slug-availability?${params.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });
  return readJson<BackofficeLinkSlugAvailabilityResponse>(
    response,
    `Failed to validate slug (${response.status})`
  );
}

export async function updateBackofficeLink(slug: string, input: UpdateBackofficeLinkInput) {
  const response = await backofficeFetch(`/links/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return readJson<{ link: BackofficeLink }>(response, `Failed to update link (${response.status})`);
}

export async function updateBackofficeLinkQrCode(
  slug: string,
  input: UpdateBackofficeLinkQrCodeInput
) {
  const response = await backofficeFetch(`/links/${encodeURIComponent(slug)}/qr-code`, {
    method: 'PATCH',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return readJson<{ link: BackofficeLink }>(
    response,
    `Failed to update QR code (${response.status})`
  );
}

export async function archiveBackofficeLink(slug: string) {
  const response = await backofficeFetch(`/links/${encodeURIComponent(slug)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  return readJson<{ success: boolean }>(response, `Failed to archive link (${response.status})`);
}

export async function restoreBackofficeLink(slug: string, input: RestoreBackofficeLinkInput) {
  const response = await backofficeFetch(`/links/${encodeURIComponent(slug)}/restore`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return readJson<{ link: BackofficeLink }>(
    response,
    `Failed to restore link (${response.status})`
  );
}

export async function searchBackofficeStudies(query: string, signal?: AbortSignal) {
  const response = await backofficeFetch(`/links/studies?query=${encodeURIComponent(query)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });
  return readJson<BackofficeStudySearchResponse>(
    response,
    `Failed to search studies (${response.status})`
  );
}
